import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Upload, X, Eye, EyeOff, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { uploadImage, resolveImageUrl } from '../../lib/imageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClassesManagement = () => {
  const [classes, setClasses] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instructor: '',
    levels: ['Beginner'],
    duration: 60,
    capacity: 15,
    day_of_week: 'Monday',
    time: '18:00',
    class_date: '',
    price: 500,
    image_url: '',
    is_collab: false
  });

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/classes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast.error('Failed to load classes');
    }
  };

  const handleToggleVisibility = async (danceClass) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/classes/${danceClass.id}/visibility`, { is_hidden: !danceClass.is_hidden }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(danceClass.is_hidden ? 'Class is now visible to visitors' : 'Class hidden from visitors');
      fetchClasses();
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.levels || formData.levels.length === 0) {
      toast.error('Please select at least one level');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingClass) {
        await axios.put(`${API}/classes/${editingClass.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Class updated successfully');
      } else {
        await axios.post(`${API}/classes`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Class created successfully');
      }
      
      fetchClasses();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving class:', error);
      toast.error('Failed to save class');
    }
  };

  const handleDelete = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/classes/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Class deleted successfully');
      fetchClasses();
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error('Failed to delete class');
    }
  };

  const handleEdit = (danceClass) => {
    setEditingClass(danceClass);
    setFormData({
      name: danceClass.name,
      description: danceClass.description,
      instructor: danceClass.instructor,
      levels: (danceClass.levels && danceClass.levels.length ? danceClass.levels : [danceClass.level]).filter(Boolean),
      duration: danceClass.duration,
      capacity: danceClass.capacity,
      day_of_week: danceClass.day_of_week,
      time: danceClass.time,
      class_date: danceClass.class_date || '',
      price: danceClass.price ?? 500,
      image_url: danceClass.image_url || '',
      is_collab: danceClass.is_collab || false
    });
    setIsDialogOpen(true);
  };

  const handleDuplicate = (danceClass) => {
    setEditingClass(null);
    setFormData({
      name: danceClass.name,
      description: danceClass.description,
      instructor: danceClass.instructor,
      levels: (danceClass.levels && danceClass.levels.length ? danceClass.levels : [danceClass.level]).filter(Boolean),
      duration: danceClass.duration,
      capacity: danceClass.capacity,
      day_of_week: danceClass.day_of_week,
      time: danceClass.time,
      class_date: '',
      price: danceClass.price ?? 500,
      image_url: danceClass.image_url || '',
      is_collab: danceClass.is_collab || false
    });
    setIsDialogOpen(true);
    toast.info('Set a new date & time, then save to create the duplicate class');
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingClass(null);
    setFormData({
      name: '',
      description: '',
      instructor: '',
      levels: ['Beginner'],
      duration: 60,
      capacity: 15,
      day_of_week: 'Monday',
      time: '18:00',
      class_date: '',
      price: 500,
      image_url: '',
      is_collab: false
    });
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleLevel = (lv) => {
    setFormData((p) => ({
      ...p,
      levels: p.levels.includes(lv) ? p.levels.filter((x) => x !== lv) : [...p.levels, lv],
    }));
  };

  const handleImage = async (file) => {
    if (!file) return;
    try {
      const url = await uploadImage(file);
      setFormData((p) => ({ ...p, image_url: url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to upload image');
    }
  };

  const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const handleDateChange = (value) => {
    if (value) {
      const wd = WEEKDAYS[new Date(value + 'T00:00:00').getDay()];
      setFormData((p) => ({ ...p, class_date: value, day_of_week: wd }));
    } else {
      setFormData((p) => ({ ...p, class_date: '' }));
    }
  };

  const formatDate = (d) => {
    if (!d) return null;
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const visibleClasses = classes.filter((c) => !c.is_hidden);
  const hiddenClasses = classes.filter((c) => c.is_hidden);

  const renderClassCard = (danceClass) => (
    <div
      key={danceClass.id}
      className="bg-white border border-zinc-200 overflow-hidden hover:border-black transition-colors"
      data-testid={`class-card-${danceClass.id}`}
    >
      <div className="relative aspect-[3/4] bg-cover bg-center bg-zinc-100" style={{ backgroundImage: `url('${resolveImageUrl(danceClass.image_url)}')` }}>
        {danceClass.is_hidden && (
          <span
            className="absolute top-2 left-2 text-[10px] uppercase tracking-wide bg-zinc-900/90 text-white px-2 py-1 rounded-full flex items-center gap-1"
            data-testid={`class-hidden-badge-${danceClass.id}`}
          >
            <EyeOff size={12} /> Hidden
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--heading-font)' }}>
          {danceClass.name}
        </h3>
        <div className="flex flex-wrap gap-1 mb-2" data-testid={`class-levels-${danceClass.id}`}>
          {(danceClass.levels && danceClass.levels.length ? danceClass.levels : [danceClass.level]).filter(Boolean).map((lv) => (
            <span key={lv} className="text-[10px] uppercase tracking-wide bg-zinc-900 text-white px-2 py-0.5 rounded-full">{lv}</span>
          ))}
        </div>
        <p className="text-sm text-zinc-600 mb-3 line-clamp-2">{danceClass.description}</p>

        <div className="space-y-1 text-sm mb-4">
          {danceClass.class_date && (
            <p data-testid={`class-date-${danceClass.id}`}><strong>Date:</strong> {formatDate(danceClass.class_date)}</p>
          )}
          <p><strong>Day:</strong> {danceClass.day_of_week}</p>
          <p><strong>Time:</strong> {danceClass.time}</p>
          <p><strong>Duration:</strong> {danceClass.duration} mins</p>
          <p><strong>Instructor:</strong> {danceClass.instructor}</p>
          <p><strong>Price:</strong> ₱{Number(danceClass.price || 0).toLocaleString()}</p>
          <p data-testid={`class-slots-remaining-${danceClass.id}`}>
            <strong>Slots Remaining:</strong>{' '}
            {danceClass.sold_out ? (
              <span className="text-red-600 font-bold">SOLD OUT</span>
            ) : (
              <span className="text-green-700 font-bold">{danceClass.slots_remaining} / {danceClass.capacity}</span>
            )}{' '}
            <span className="text-zinc-400">({danceClass.confirmed_count} confirmed)</span>
          </p>
        </div>

        <div className="space-y-2">
          <button
            onClick={() => handleToggleVisibility(danceClass)}
            className={`w-full rounded px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${danceClass.is_hidden ? 'bg-green-100 hover:bg-green-200 text-green-700' : 'bg-amber-100 hover:bg-amber-200 text-amber-700'}`}
            data-testid={`toggle-visibility-class-${danceClass.id}`}
          >
            {danceClass.is_hidden ? <><Eye size={16} /> SHOW TO VISITORS</> : <><EyeOff size={16} /> HIDE FROM VISITORS</>}
          </button>
          <button
            onClick={() => handleDuplicate(danceClass)}
            className="w-full bg-zinc-900 hover:bg-black text-white rounded px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2"
            data-testid={`duplicate-class-${danceClass.id}`}
          >
            <Copy size={16} /> DUPLICATE (NEW DATE)
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => handleEdit(danceClass)}
              className="flex-1 bg-zinc-100 hover:bg-zinc-200 text-black rounded px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2"
              data-testid={`edit-class-${danceClass.id}`}
            >
              <Edit size={16} />
              EDIT
            </button>
            <button
              onClick={() => handleDelete(danceClass.id)}
              className="flex-1 bg-red-100 hover:bg-red-200 text-red-600 rounded px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2"
              data-testid={`delete-class-${danceClass.id}`}
            >
              <Trash2 size={16} />
              DELETE
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-8" data-testid="classes-management">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
          Classes Management
        </h2>
        <button
          onClick={() => setIsDialogOpen(true)}
          className="bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 flex items-center gap-2"
          data-testid="add-class-btn"
        >
          <Plus size={20} />
          ADD CLASS
        </button>
      </div>

      {/* Visible Classes */}
      <div className="flex items-center gap-2 mb-4" data-testid="visible-classes-header">
        <Eye size={18} className="text-zinc-500" />
        <h3 className="text-lg font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
          Visible to Visitors
        </h3>
        <span className="text-sm text-zinc-400">({visibleClasses.length})</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleClasses.length === 0 ? (
          <p className="text-zinc-400 text-sm" data-testid="no-visible-classes">No visible classes.</p>
        ) : (
          visibleClasses.map(renderClassCard)
        )}
      </div>

      {/* Hidden Classes */}
      {hiddenClasses.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-4 mt-12 pt-8 border-t border-zinc-200" data-testid="hidden-classes-header">
            <EyeOff size={18} className="text-zinc-500" />
            <h3 className="text-lg font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
              Hidden from Visitors
            </h3>
            <span className="text-sm text-zinc-400">({hiddenClasses.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-75">
            {hiddenClasses.map(renderClassCard)}
          </div>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="class-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
              {editingClass ? 'Edit Class' : 'Add New Class'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Class Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                data-testid="class-name-input"
              />
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                data-testid="class-description-input"
              />
            </div>

            <div>
              <Label htmlFor="instructor">Instructor *</Label>
              <Input
                id="instructor"
                name="instructor"
                value={formData.instructor}
                onChange={handleInputChange}
                required
                data-testid="class-instructor-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Levels * (select one or more)</Label>
                <div className="flex flex-wrap gap-2 mt-1" data-testid="class-levels-group">
                  {['Beginner', 'Intermediate', 'Advanced', 'All Levels'].map((lv) => {
                    const active = formData.levels.includes(lv);
                    return (
                      <button
                        type="button"
                        key={lv}
                        onClick={() => toggleLevel(lv)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-black text-white border-black' : 'bg-white text-zinc-600 border-zinc-300 hover:border-black'}`}
                        data-testid={`class-level-toggle-${lv.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {lv}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="duration">Duration (mins) *</Label>
                <Input
                  id="duration"
                  name="duration"
                  type="number"
                  value={formData.duration}
                  onChange={handleInputChange}
                  required
                  data-testid="class-duration-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="class_date">Class Date</Label>
              <Input
                id="class_date"
                name="class_date"
                type="date"
                value={formData.class_date}
                onChange={(e) => handleDateChange(e.target.value)}
                data-testid="class-date-input"
              />
              <p className="text-xs text-zinc-500 mt-1">Pick the specific date this class runs. Classes are listed in date order. Leave blank for a recurring weekly class.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="day_of_week">Day of Week *</Label>
                <Select value={formData.day_of_week} onValueChange={(value) => setFormData({ ...formData, day_of_week: value })}>
                  <SelectTrigger data-testid="class-day-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monday">Monday</SelectItem>
                    <SelectItem value="Tuesday">Tuesday</SelectItem>
                    <SelectItem value="Wednesday">Wednesday</SelectItem>
                    <SelectItem value="Thursday">Thursday</SelectItem>
                    <SelectItem value="Friday">Friday</SelectItem>
                    <SelectItem value="Saturday">Saturday</SelectItem>
                    <SelectItem value="Sunday">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="time">Time *</Label>
                <Input
                  id="time"
                  name="time"
                  type="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                  data-testid="class-time-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="capacity">Capacity *</Label>
                <Input
                  id="capacity"
                  name="capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={handleInputChange}
                  required
                  data-testid="class-capacity-input"
                />
              </div>
              <div>
                <Label htmlFor="price">Price (₱) *</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  min="0"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  data-testid="class-price-input"
                />
              </div>
            </div>

            <div>
              <Label>Class Image</Label>
              <p className="text-xs text-zinc-500 mb-2">Upload a photo (up to 16MB — large images are optimized automatically) or paste an image URL.</p>
              {formData.image_url && (
                <div className="relative w-40 mb-2" data-testid="class-image-preview">
                  <img src={resolveImageUrl(formData.image_url)} alt="Class preview" className="aspect-[3/4] w-full object-cover border border-zinc-200" />
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, image_url: '' }))}
                    className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1"
                    data-testid="remove-class-image-btn"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              <label htmlFor="class-image-file" className="flex items-center gap-2 cursor-pointer bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 px-4 py-3 w-fit transition-colors" data-testid="class-image-upload-label">
                <Upload size={18} /> Upload a photo
                <input id="class-image-file" type="file" accept="image/*" className="hidden" onChange={(e) => handleImage(e.target.files?.[0])} data-testid="class-image-upload-input" />
              </label>
              <Input
                id="image_url"
                name="image_url"
                value={formData.image_url}
                onChange={handleInputChange}
                placeholder="...or paste an image URL"
                className="mt-2"
                data-testid="class-image-input"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer border border-zinc-200 rounded p-3" data-testid="class-collab-label">
              <input
                type="checkbox"
                checked={!!formData.is_collab}
                onChange={(e) => setFormData({ ...formData, is_collab: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-black shrink-0"
                data-testid="class-collab-checkbox"
              />
              <span className="text-sm">
                <span className="font-medium">Collab class</span>
                <span className="block text-xs text-zinc-500">Priced higher — visitors cannot pay with a Class Pass; only GCash / bank transfer.</span>
              </span>
            </label>

            <div className="flex gap-2 pt-4">
              <button
                type="submit"
                className="flex-1 bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95"
                data-testid="save-class-btn"
              >
                {editingClass ? 'UPDATE' : 'CREATE'}
              </button>
              <button
                type="button"
                onClick={handleCloseDialog}
                className="flex-1 border-2 border-black text-black hover:bg-black hover:text-white rounded-full px-6 py-3 transition-colors"
                data-testid="cancel-class-btn"
              >
                CANCEL
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClassesManagement;